import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Divider,
  Container,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
  CircularProgress,
  Skeleton,
  useMediaQuery,
  LinearProgress
} from '@mui/material';
import {
  PhotoCamera,
  Delete as DeleteIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  LinkedIn as LinkedInIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  FormatQuote as FormatQuoteIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { profileStyles } from './styles/profileStyles';
import { alpha } from '@mui/material/styles';
import userService from '../../services/userService';
import uploadService from '../../services/uploadService';

// Import dialog components
import ChangePassword from './dialogs/ChangePassword';
import ViewProfile from './dialogs/ViewProfile';

// Enhanced Profile Setting component
const ProfileSetting = () => {
  const theme = useTheme();
  const styles = profileStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // File input ref
  const fileInputRef = useRef(null);
  
  // Loading states for API integration
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Initial profile data
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    role: '',
    profileImage: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    quote: '',
    about: '',
    isVerified: false
  });
  
  // Form state
  const [formValues, setFormValues] = useState({ ...profileData });
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Dialog state
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  
  const MotionDiv = motion.div;

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: 'spring', 
        stiffness: 100 
      } 
    }
  };

  // Retrieve initial user data from token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        
        // Initial data from token
        const initialData = {
          firstName: tokenData.first_name || '',
          lastName: tokenData.last_name || '',
          username: tokenData.username || '',
          email: tokenData.email || '',
          role: tokenData.role || '',
          isVerified: tokenData.isVerified || false
        };
        
        // Set initial state from token
        setProfileData(prevData => ({
          ...prevData,
          ...initialData
        }));
        setFormValues(prevValues => ({
          ...prevValues,
          ...initialData
        }));
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }, []);

  // Fetch complete profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        console.log('Fetching profile data...');
        const response = await userService.getUserDetails();
        console.log('Received user data:', response);
      
        if (!response || !response.user) {
          throw new Error('No user data received');
        }

        const userData = response.user;
        
        // Get branch from JWT token
        const token = localStorage.getItem('token');
        let branchName = "Main Branch";
        
        if (token) {
          try {
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            branchName = tokenData.branch || "Main Branch";
          } catch (error) {
            console.error('Error parsing token:', error);
          }
        }
      
        // Transform and set both frontend and backend field names
        const formattedData = {
          // Frontend field names
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          username: userData.username || '',
          email: userData.email || '',
          image: userData.image || '',
          facebook: userData.facebook || '',
          instagram: userData.instagram || '',
          twitter: userData.x_twitter || '',
          linkedin: userData.linkedin || '',
          quote: userData.quote || '',
          about: userData.about_me || '',
          branch: branchName, // Add branch name
          // Backend field names for syncing
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          x_twitter: userData.x_twitter || '',
          about_me: userData.about_me || ''
        };
    
        setProfileData(formattedData);
        setFormValues(formattedData);
        setImagePreview(userData.image || '');
        
        console.log('Updated form values:', formattedData);
        
      } catch (error) {
        console.error('Error fetching profile:', error);
        setNotification({
          open: true,
          message: 'Failed to load profile data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProfileData();
  }, []);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => {
      const updates = {
        ...prev,
        [name]: value
      };
  
      // Map frontend field names to backend field names
      switch (name) {
        case 'firstName':
          updates.first_name = value;
          updates.firstName = value;
          break;
        case 'lastName':
          updates.last_name = value;
          updates.lastName = value;
          break;
        case 'twitter':
          updates.x_twitter = value;
          updates.twitter = value;
          break;
        case 'about':
          updates.about_me = value;
          updates.about = value;
          break;
      }
  
      return updates;
    });
  };

  // Handle image selection
  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      try {
        // Validate file
        const validation = uploadService.validateImage(file);
        if (!validation.isValid) {
          setNotification({
            open: true,
            message: validation.message,
            severity: 'error'
          });
          return;
        }
        
        setProfileImageFile(file);
        
        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing image:', error);
        setNotification({
          open: true,
          message: 'Error processing image: ' + error.message,
          severity: 'error'
        });
      }
    }
  };
  
  // Handle image remove
  const handleRemoveImage = () => {
    setProfileImageFile(null);
    setImagePreview(profileData.profileImage);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Show upload progress if there's a new image
      if (profileImageFile) {
        setIsUploading(true);
        setUploadProgress(0);
        
        // Update progress periodically to provide visual feedback
        const interval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(interval);
              return 90;
            }
            return prev + 10;
          });
        }, 200);
      }
      
      // Call the API to update profile
      const updatedProfile = await userService.updateUser(
        formValues, 
        profileImageFile
      );
      
      // Update progress to 100%
      if (profileImageFile) {
        setUploadProgress(100);
        setTimeout(() => {
          setIsUploading(false);
        }, 500);
      }
      
      // Update local state with the response from the server
      if (updatedProfile && updatedProfile.user) {
        const responseData = updatedProfile.user;
        
        const updatedData = {
          ...profileData,
          // Frontend field names
          firstName: responseData.first_name || '',
          lastName: responseData.last_name || '',
          username: responseData.username || '',
          email: responseData.email || '',
          image: responseData.image || '',
          facebook: responseData.facebook || '',
          instagram: responseData.instagram || '',
          twitter: responseData.x_twitter || '',
          linkedin: responseData.linkedin || '',
          quote: responseData.quote || '',
          about: responseData.about_me || '',
          // Backend field names
          first_name: responseData.first_name || '',
          last_name: responseData.last_name || '',
          x_twitter: responseData.x_twitter || '',
          about_me: responseData.about_me || ''
        };
        
        // Update both state objects with the same data
        setProfileData(updatedData);
        setFormValues(updatedData);
        
        if (responseData.image) {
          setImagePreview(responseData.image);
        }
        
        setProfileImageFile(null);
        
        // Show success notification
        setNotification({
          open: true,
          message: updatedProfile.message || 'Profile updated successfully!',
          severity: 'success'
        });

        // **FIXED: Notify Header component about profile update**
        window.dispatchEvent(new CustomEvent('profile-updated', {
          detail: { 
            image: responseData.image,
            username: responseData.username,
            first_name: responseData.first_name,
            last_name: responseData.last_name,
            email: responseData.email
          }
        }));
        
        console.log('✅ Profile update event dispatched:', {
          image: responseData.image,
          username: responseData.username
        });
      }
      
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // **ENHANCED: Better error handling for different scenarios**
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (error.response?.status === 409) {
        // Conflict error (email/username already exists)
        errorMessage = error.response?.data?.message || 'Email or username already in use by another account.';
      } else if (error.response?.status === 400) {
        // Bad request
        errorMessage = error.response?.data?.message || 'Invalid data provided. Please check your inputs.';
      } else if (error.response?.status === 413) {
        // File too large
        errorMessage = 'Image file is too large. Please choose a smaller image (max 5MB).';
      } else if (error.response?.status === 500) {
        // Server error
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.message?.includes('Network Error')) {
        // Network error
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      }
      
      // Show error notification
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Close notification
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Render loading skeleton when data is loading
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={styles.header}>
          <Box>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={300} height={24} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton variant="rounded" width={120} height={40} />
            <Skeleton variant="rounded" width={150} height={40} />
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={styles.section}>
              <Skeleton variant="text" width={150} height={32} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
                <Skeleton variant="circular" width={120} height={120} />
              </Box>
              <Skeleton variant="text" width={150} height={32} sx={{ mt: 4 }} />
              <Box sx={{ mt: 2 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i} sx={{ mt: i > 1 ? 2 : 0, display: 'flex', alignItems: 'center' }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1 }} />
                    <Skeleton variant="rounded" height={40} width="100%" />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Paper sx={styles.section}>
              <Skeleton variant="text" width={150} height={32} />
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Grid item xs={12} sm={i <= 4 ? 6 : 12} key={i}>
                    <Skeleton variant="text" width={100} height={24} />
                    <Skeleton variant="rounded" height={56} width="100%" sx={{ mt: 1 }} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
            
            <Paper sx={{...styles.section, mt: 3}}>
              <Skeleton variant="text" width={150} height={32} />
              <Skeleton variant="rounded" height={200} width="100%" sx={{ mt: 2 }} />
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Skeleton variant="rounded" width={150} height={48} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <MotionDiv
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <MotionDiv variants={itemVariants}>
          <Box sx={styles.header}>
            <Box>
              <Typography variant="h4" component="h1" sx={styles.title}>
                Profile Settings
              </Typography>
              <Typography variant="body1" sx={styles.subtitle}>
                Manage your personal information and account preferences
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<VisibilityIcon />}
                onClick={() => setOpenProfileDialog(true)}
                sx={styles.actionButton}
              >
                View Profile
              </Button>
              
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LockIcon />}
                onClick={() => setOpenPasswordDialog(true)}
                sx={styles.actionButton}
              >
                Change Password
              </Button>
            </Box>
          </Box>
        </MotionDiv>


        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left column */}
            <Grid item xs={12} md={4}>
              <MotionDiv variants={itemVariants}>
                <Paper sx={styles.section}>
                  <Typography variant="h6" sx={styles.sectionTitle}>
                    Profile Picture
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={styles.avatarWrapper}>
                      <Avatar 
                        src={imagePreview} 
                        alt={`${formValues.firstName} ${formValues.lastName}`} 
                        sx={styles.avatar}
                      />
                      
                      {isUploading && (
                        <Box sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          overflow: 'hidden',
                          borderBottomLeftRadius: '50%',
                          borderBottomRightRadius: '50%',
                        }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={uploadProgress}
                            sx={{
                              height: '100%',
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.primary.main,
                              }
                            }}
                          />
                        </Box>
                      )}
                      
                      <Box sx={styles.avatarButtons}>
                        <Tooltip title="Upload photo">
                          <IconButton 
                            color="primary" 
                            aria-label="upload picture"
                            onClick={() => fileInputRef.current.click()}
                            sx={{ 
                              bgcolor: 'background.paper',
                              boxShadow: 2
                            }}
                            size="small"
                            disabled={isUploading}
                          >
                            <PhotoCamera fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {imagePreview && imagePreview !== profileData.profileImage && (
                          <Tooltip title="Remove photo">
                            <IconButton 
                              color="error" 
                              aria-label="remove picture"
                              onClick={handleRemoveImage}
                              sx={{ 
                                bgcolor: 'background.paper',
                                boxShadow: 2
                              }}
                              size="small"
                              disabled={isUploading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    
                    <input
                      ref={fileInputRef}
                      accept="image/*"
                      type="file"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                      Recommended size: 300x300px <br />
                      Max file size: 5MB
                    </Typography>
                  </Box>

                  <Typography variant="h6" sx={{...styles.sectionTitle, mt: 4}}>
                    Social Media
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Box sx={styles.socialField}>
                      <FacebookIcon sx={styles.socialIcon} />
                      <TextField
                        name="facebook"
                        label="Facebook"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={formValues.facebook || ''}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/username"
                        sx={styles.inputField}
                      />
                    </Box>
                    
                    <Box sx={{ mt: 2, ...styles.socialField }}>
                      <InstagramIcon sx={styles.socialIcon} />
                      <TextField
                        name="instagram"
                        label="Instagram"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={formValues.instagram || ''}
                        onChange={handleInputChange}
                        placeholder="https://instagram.com/username"
                        sx={styles.inputField}
                      />
                    </Box>
                    
                    <Box sx={{ mt: 2, ...styles.socialField }}>
                      <XIcon sx={styles.socialIcon} />
                      <TextField
                        name="twitter"
                        label="X"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={formValues.twitter || ''}
                        onChange={handleInputChange}
                        placeholder="https://x.com/username"
                        sx={styles.inputField}
                      />
                    </Box>
                    
                    <Box sx={{ mt: 2, ...styles.socialField }}>
                      <LinkedInIcon sx={styles.socialIcon} />
                      <TextField
                        name="linkedin"
                        label="LinkedIn"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={formValues.linkedin || ''}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/in/username"
                        sx={styles.inputField}
                      />
                    </Box>
                  </Box>
                </Paper>
              </MotionDiv>
            </Grid>
            
            {/* Right column */}
            <Grid item xs={12} md={8}>
              <MotionDiv variants={itemVariants}>
                <Paper sx={styles.section}>
                  <Typography variant="h6" sx={styles.sectionTitle}>
                    Basic Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.formField}>
                        <Typography sx={styles.formLabel}>First Name</Typography>
                        <TextField
                          name="firstName"
                          variant="outlined"
                          fullWidth
                          required
                          value={formValues.firstName}
                          onChange={handleInputChange}
                          sx={styles.inputField}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.formField}>
                        <Typography sx={styles.formLabel}>Last Name</Typography>
                        <TextField
                          name="lastName"
                          variant="outlined"
                          fullWidth
                          required
                          value={formValues.lastName}
                          onChange={handleInputChange}
                          sx={styles.inputField}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.formField}>
                        <Typography sx={styles.formLabel}>Username</Typography>
                        <TextField
                          name="username"
                          variant="outlined"
                          fullWidth
                          required
                          value={formValues.username}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">@</InputAdornment>
                            ),
                            endAdornment: formValues.isVerified && (
                              <InputAdornment position="end">
                                <Tooltip title="Verified Account">
                                  <VerifiedUserIcon color="primary" fontSize="small" />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          sx={styles.inputField}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.formField}>
                        <Typography sx={styles.formLabel}>Email Address</Typography>
                        <TextField
                          name="email"
                          type="email"
                          variant="outlined"
                          fullWidth
                          required
                          value={formValues.email}
                          onChange={handleInputChange}
                          sx={styles.inputField}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={styles.formField}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <FormatQuoteIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                          <Typography component="span" sx={styles.formLabel}>
                            Personal Quote
                          </Typography>
                        </Box>
                        <TextField
                          name="quote"
                          variant="outlined"
                          fullWidth
                          multiline
                          maxRows={2}
                          value={formValues.quote || ''}
                          onChange={handleInputChange}
                          placeholder="Share a quote that inspires you"
                          sx={styles.inputField}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </MotionDiv>
              
              <MotionDiv variants={itemVariants}>
                <Paper sx={{...styles.section, mt: 3}}>
                  <Typography variant="h6" sx={styles.sectionTitle}>
                    About Me
                  </Typography>
                  
                  <Box sx={styles.formField}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Tell others about yourself, your interests, and your experience
                    </Typography>
                    <TextField
                      name="about"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={6}
                      value={formValues.about || ''}
                      onChange={handleInputChange}
                      sx={styles.inputField}
                    />
                  </Box>
                </Paper>
              </MotionDiv>
              
              <MotionDiv variants={itemVariants} style={{ marginTop: 24 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    type="submit"
                    disabled={isSaving || isUploading}
                    sx={{
                      ...styles.submitButton,
                      px: 4,
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </MotionDiv>
            </Grid>
          </Grid>
        </form>
      </MotionDiv>

      {/* Dialogs */}
      <ChangePassword 
        open={openPasswordDialog} 
        onClose={() => setOpenPasswordDialog(false)} 
      />

      <ViewProfile 
        open={openProfileDialog} 
        onClose={() => setOpenProfileDialog(false)}
        profileData={profileData}
      />

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ 
            boxShadow: 3,
            borderRadius: 2,
            width: '100%' 
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfileSetting;